import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { AudioPlayer, Button, Card, EmptyState, Icon, Skeleton, Tag } from "@xinghai/ui";
import { recordingAudioUrl, studentApi } from "../../lib/api";
import type { RecordingListItem } from "../../lib/api";
import { qk } from "../../lib/queries";
import { TASK_LABEL, displayPinyin, formatDuration, recordingStatus } from "./taskMeta";
import { PendingUploads } from "./PendingUploads";
import { AiScoreCard } from "../ai/AiScoreCard";
import { aiPollInterval } from "../ai/poll";

/** Shared recording card. Teachers pass an expert section as `children`. */
export function RecordingListRow({ r, children }: { r: RecordingListItem; children?: ReactNode }) {
  const status = recordingStatus(r);
  return (
    <Card padding="md" className="xz-reclist__item">
      <div className="xz-reclist__head">
        <Tag tone={status.tone}>
          <Icon id={status.icon} size={16} /> {status.label}
        </Tag>
        <span className="gz-caption">{TASK_LABEL[r.task]}</span>
        <span className="gz-caption">{formatDuration(r.duration_ms)}</span>
      </div>
      <p className="xz-reclist__text">{r.material_text ?? r.material_title ?? r.material_id}</p>
      {r.material_pinyin && (
        <p className="gz-caption xz-reclist__pinyin">{displayPinyin(r.material_pinyin)}</p>
      )}
      <AiScoreCard ai={r.ai} text={r.material_text} />
      <AudioPlayer
        src={recordingAudioUrl(r.id)}
        hideRate
        size="sm"
        label={`播放 ${TASK_LABEL[r.task]} 录音`}
      />
      {r.state === "qc_reject" && (
        <div className="xz-reclist__reject">
          <p className="gz-notice gz-notice--error">
            <Icon id="ui-info" size={20} />
            {status.note}
          </p>
          {r.assignment_id && (
            <Link className="gz-button gz-button--primary" to={`/s/task/${r.assignment_id}`}>
              <Icon id="ui-refresh" size={20} /> 重录这一条
            </Link>
          )}
        </div>
      )}
      {children}
    </Card>
  );
}

/** docs/UX.md §1.4 — 默认只显示「需重录」，其他折叠. */
export function RecordingsPage() {
  const q = useQuery({
    queryKey: qk.recordings,
    queryFn: ({ signal }) => studentApi.recordings(signal),
    refetchInterval: (query) => aiPollInterval(query.state.data?.recordings ?? []),
  });
  const rows = useMemo(() => q.data?.recordings ?? [], [q.data]);
  const [showOthers, setShowOthers] = useState(false);
  const expandedForAi = useRef(false);

  const needRedo = rows.filter((r) => r.state === "qc_reject");
  const others = rows.filter((r) => r.state !== "qc_reject");
  const othersHaveAi = others.some((r) => r.ai);
  useEffect(() => {
    if (othersHaveAi && !expandedForAi.current) {
      expandedForAi.current = true;
      setShowOthers(true);
    }
  }, [othersHaveAi]);

  if (q.isPending) {
    return (
      <div className="xz-container xz-stack" aria-busy="true">
        <Skeleton variant="rect" height={120} />
        <Skeleton variant="rect" height={120} />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="xz-container xz-stack">
        <h1 className="gz-type-heading">我的录音</h1>
        <PendingUploads />
        <EmptyState
          icon={
            <img
              className="xz-mascot"
              src="/design-system/assets/brand-library/mascot-side.png"
              alt=""
              aria-hidden="true"
              width={160}
              height={160}
            />
          }
          title="还没有录音"
          description="去首页录第一条吧。"
          action={
            <Link className="gz-button gz-button--primary" to="/s">
              去录音
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="xz-container xz-stack">
      <h1 className="gz-type-heading">我的录音</h1>

      {/* docs/ROADMAP.md B2 — 「待上传 n 条」，展开能看到每一条 */}
      <PendingUploads compact />

      {needRedo.length > 0 ? (
        <>
          <p className="gz-notice">
            <Icon id="ui-refresh" size={20} />有 {needRedo.length}{" "}
            条需要重录。重录一次就好，不影响成绩。
          </p>
          {needRedo.map((r) => (
            <RecordingListRow key={r.id} r={r} />
          ))}
        </>
      ) : (
        <p className="gz-notice">
          <Icon id="ui-check-circle" size={20} />
          没有需要重录的，都挺好。
        </p>
      )}

      {others.length > 0 && (
        <>
          <Button
            variant="ghost"
            aria-expanded={showOthers}
            onClick={() => setShowOthers((v) => !v)}
          >
            <Icon id={showOthers ? "ui-collapse" : "ui-expand"} size={20} />
            {showOthers ? "收起其他录音" : `查看其他 ${others.length} 条录音`}
          </Button>
          {showOthers && others.map((r) => <RecordingListRow key={r.id} r={r} />)}
        </>
      )}
    </div>
  );
}
